// Dependencies
import React, { useState } from 'react';
import { navigate } from 'gatsby';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import AddToMailchimp from 'gatsby-plugin-mailchimp';

// Components
import Container from '../../container/container';
import InputFactory from '../../forms/inputFactory/inputFactory';
import FormError from '../../forms/formError/formError';
import Image from '../../image/image';

// Validators
import validateEnquiry from '../../../validators/enquiry';

// JSON data
import countries from '../../../json/countries.json';
import states from '../../../json/states.json';

// Styles
import EnquireFormStyles from './enquireForm.styles';

export default function EnquireForm({ layout }) {
  const partnershipBrandName = layout?.partnershipBrandName;
  const partnership = partnershipBrandName || false;
  const eventName = layout?.eventName;
  const maclynnEvent = eventName || false;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    telephone: '',
    occupation: '',
    country: '',
    state: '',
    photos: [],
    referredBy: '',
    message: '',
    newsletter: false,
    partnership,
    event: maclynnEvent,
    gclid: '',
    clientId: '',
  });
  const [formDataErrors, setFormDataErrors] = useState({});
  const [formError, setFormError] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [showEventFormSuccess, setShowEventFormSuccess] = useState(false);

  const heading = layout?.heading;
  const text = layout?.text;
  const text2 = layout?.text2;
  const image = layout?.image;
  const quote = layout?.quote;
  const quoteByline = layout?.quoteByline;

  const validate = () => {
    setFormDataErrors({});

    const validatedEnquiry = validateEnquiry(formData);

    if (!validatedEnquiry?.success) {
      setSubmitDisabled(false);

      if (validatedEnquiry?.errors) {
        setFormDataErrors(validatedEnquiry?.errors);
      } else {
        setFormError(true);
      }

      return false;
    }

    return true;
  };

  const sendToMonday = async () => {
    // Send request to our API - creates the item in Monday.com
    const request = await fetch(
      `${process.env.GATSBY_SITE_URL}/api/enquire/create-item`,
      {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
        }),
      },
    )
      .then((res) => res.json())
      .catch();

    if (!request?.success) {
      setFormError(true);
      setSubmitDisabled(false);

      return false;
    }

    const photos = formData?.photos;

    // Check if any photos were uploaded
    if (photos && photos.length) {
      photos.map(async (photo, i) => {
        // Create formData for multipart request
        const multipartForm = new FormData();
        multipartForm.append('itemId', request?.itemId);
        multipartForm.append(`fileUpload${i}`, photo);

        // Send request to our API - uploads files to Monday.com & attaches them to item
        fetch(`${process.env.GATSBY_SITE_URL}/api/enquire/upload-photos`, {
          method: 'POST',
          body: multipartForm,
        });
      });
    }

    return true;
  };

  const sendBackupEmail = () => {
    fetch(`${process.env.GATSBY_SITE_URL}/api/emails/enquire/admin`, {
      method: 'POST',
      body: JSON.stringify({
        formData,
        partnership,
      }),
    }).catch();
  };

  const sendAutoresponder = () => {
    fetch(`${process.env.GATSBY_SITE_URL}/api/emails/enquire/user`, {
      method: 'POST',
      body: JSON.stringify({
        formData,
      }),
    }).catch();
  };

  const subscribeToNewsletter = () => {
    AddToMailchimp(formData?.email)
      .then((response) => {
        if (response?.result !== 'success') {
          setFormData((fields) => ({
            ...fields,
            newsletter: false,
          }));
        }
      })
      .catch();
  };

  const setAnalyticsFormData = async () => {
    if (typeof document !== 'undefined') {
      const gclid = document.querySelector('input[name="gclid"]').value;
      const clientId = document.querySelector('input[name="client_id"]').value;

      setFormData((fields) => ({
        ...fields,
        gclid,
        clientId,
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!submitDisabled) {
      const validates = validate();

      if (validates) {
        setSubmitDisabled(true);

        sendBackupEmail();
        sendAutoresponder();

        if (formData.newsletter === true) subscribeToNewsletter();

        if (await sendToMonday()) {
          if (!maclynnEvent) {
            await navigate('/thank-you');
          } else if (typeof window !== 'undefined') {
            setShowEventFormSuccess(true);

            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 4000);

            setTimeout(() => {
              window.location.reload();
            }, 6000);
          }
        }
      }
    }
  };

  const handleChange = (event, actionMeta) => {
    const { value } = event?.target || event;
    const { checked } = event?.target || event;
    const { name } = event?.target || actionMeta || '';

    setAnalyticsFormData();

    setFormData((fields) => ({
      ...fields,
      [name]: checked || value,
    }));
  };

  return (
    <EnquireFormStyles
      partnership={partnership}
      event={maclynnEvent}
      showEventFormSuccess={showEventFormSuccess}
      image={!!image}
    >
      <Container>
        <div className="inner">
          <div className="top">
            {text && <div className="box">{parse(text)}</div>}
          </div>

          {!!image && (
            <div className="left">
              <div className="image-wrapper">
                <Image image={image} className="image" />

                {!!quote && (
                  <div className="quote">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 34.67 27.23"
                      className="icon"
                    >
                      <g>
                        <path
                          fill="#fff"
                          strokeWidth={0}
                          d="m21.7,24.83c-5.09-5.72-1.59-15.67,2.66-19.52,3.61-3.28,9.6-5.96,10.17-5.02.9,1.49-2.64,2.55-5.29,4.94-3.32,3.59-4.43,5.2.16,7.22,4.92,2.22,5.01,4.39,5.17,7.15.4,6.95-7.89,10.21-12.86,5.23Z"
                        />
                        <path
                          fill="#fff"
                          strokeWidth={0}
                          d="m2.53,24.73C-2.57,19.02.94,9.06,5.18,5.21,8.8,1.93,14.79-.75,15.35.19c.9,1.49-2.64,2.55-5.29,4.94-3.32,3.59-4.43,5.2.16,7.22,4.92,2.22,5.01,4.39,5.17,7.15.4,6.95-7.89,10.21-12.86,5.23Z"
                        />
                      </g>
                    </svg>
                    {quote}

                    {!!quoteByline && (
                      <span className="byline">&mdash; {quoteByline}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="right">
            {heading && <h2 className="heading">{heading}</h2>}

            {text2 && <span className="intro">{parse(text2)}</span>}

            <form
              onSubmit={handleSubmit}
              encType="multipart/form-data"
              className="form"
              id="form"
            >
              <div className="form-body">
                <InputFactory
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  required
                  value={formData?.firstName}
                  onChange={handleChange}
                  error={formDataErrors?.firstName}
                />

                <InputFactory
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  required
                  value={formData?.lastName}
                  onChange={handleChange}
                  error={formDataErrors?.lastName}
                />

                <InputFactory
                  type="email"
                  name="email"
                  placeholder="Email"
                  required
                  value={formData?.email}
                  onChange={handleChange}
                  error={formDataErrors?.email}
                />

                <InputFactory
                  type="text"
                  name="telephone"
                  placeholder="Telephone"
                  required
                  value={formData?.telephone}
                  onChange={handleChange}
                  error={formDataErrors?.telephone}
                />

                <InputFactory
                  type="text"
                  name="occupation"
                  placeholder="Occupation"
                  value={formData?.occupation}
                  onChange={handleChange}
                  error={formDataErrors?.occupation}
                />

                {!maclynnEvent && (
                  <InputFactory
                    type="select"
                    name="country"
                    label="Country of Residence"
                    required
                    value={formData?.country}
                    onChange={handleChange}
                    options={countries}
                    error={formDataErrors?.country}
                  />
                )}

                {formData.country && formData?.country === 'United States' && (
                  <InputFactory
                    type="select"
                    name="state"
                    label="State"
                    value={formData?.state}
                    onChange={handleChange}
                    options={states}
                    error={formDataErrors?.state}
                  />
                )}

                {!partnership && !maclynnEvent && (
                  <InputFactory
                    type="text"
                    name="referredBy"
                    label="Did a Maclynn ambassador refer you?"
                    placeholder="Please write their name here!"
                    value={formData?.referredBy}
                    onChange={handleChange}
                    error={formDataErrors?.referredBy}
                  />
                )}

                {!partnership && !maclynnEvent && (
                  <InputFactory
                    type="file"
                    id="photos"
                    name="photos"
                    label="Photos &mdash; We'd love to know what you look like! We promise to keep them confidential"
                    value={formData?.photos}
                    accept={{
                      'image/jpeg': [],
                      'image/png': [],
                    }}
                    onChange={handleChange}
                    error={formDataErrors?.photos}
                    instructions="Please note that each file must be no larger than 4.5MB, with a limit of 4 files allowed in total."
                  />
                )}

                {!maclynnEvent && (
                  <InputFactory
                    type="textarea"
                    name="message"
                    placeholder="Please tell us a bit about yourself and what you seek in a partner"
                    value={formData?.message}
                    onChange={handleChange}
                    error={formDataErrors?.message}
                  />
                )}

                <InputFactory
                  type="checkbox"
                  name="newsletter"
                  label="Please tick this box if you would like to receive our newsletter, exclusive dating tips, offers and invites to events and workshops."
                  defaultChecked={formData?.newsletter}
                  checked={formData?.newsletter}
                  value={formData?.newsletter}
                  onChange={handleChange}
                  error={formDataErrors?.newsletter}
                />
              </div>

              <div>
                <input type="hidden" name="gclid" />
                <input type="hidden" name="client_id" />
              </div>

              <footer className="form-footer">
                {formError && <FormError />}

                <button
                  type="submit"
                  className="btn dark"
                  disabled={submitDisabled}
                >
                  Submit Enquiry
                </button>

                {maclynnEvent && (
                  <div className="success-wrapper">
                    <div className="success">
                      Thank you for submitting your details.
                    </div>
                  </div>
                )}
              </footer>
            </form>
          </div>
        </div>
      </Container>
    </EnquireFormStyles>
  );
}

EnquireForm.propTypes = {
  layout: PropTypes.shape({
    heading: PropTypes.string,
    text: PropTypes.string,
    text2: PropTypes.string,
    quote: PropTypes.string,
    quoteByline: PropTypes.string,
    image: PropTypes.shape({}),
    type: PropTypes.string,
    partnershipBrandName: PropTypes.string,
    eventName: PropTypes.string,
  }),
};

EnquireForm.defaultProps = {
  layout: {
    partnership: false,
    type: 'standard',
  },
};
